import { DirectionProvider } from "@radix-ui/react-direction";
import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { IntlProvider } from "react-intl";

import {
  DEFAULT_LOCALE,
  MESSAGES,
  directionOf,
  intlLocaleOf,
  isLocale,
  type Locale,
} from "@/shared/i18n/messages";
import { compileMessages } from "@/shared/i18n/localizedNumbers";

const STORAGE_KEY = "pmtool.locale";

/* Each catalog is parsed once, when first shown, with its counts set to print in the locale's
   digits (localizedNumbers.ts). */
const COMPILED = new Map<Locale, ReturnType<typeof compileMessages>>();
function compiledMessages(locale: Locale) {
  let messages = COMPILED.get(locale);
  if (!messages) {
    messages = compileMessages(MESSAGES[locale]);
    COMPILED.set(locale, messages);
  }
  return messages;
}

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
}

export const LocaleContext = createContext<LocaleContextValue | null>(null);

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(readStoredLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = directionOf(locale);
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // Private browsing can block storage; the choice still applies for this visit.
    }
  }, [locale]);

  const toggleLocale = useCallback(() => {
    setLocale((current) => (current === "ar" ? "en" : "ar"));
  }, []);

  const value = useMemo(() => ({ locale, setLocale, toggleLocale }), [locale, toggleLocale]);

  return (
    <LocaleContext.Provider value={value}>
      <IntlProvider
        locale={intlLocaleOf(locale)}
        messages={compiledMessages(locale)}
        defaultLocale="en"
      >
        <DirectionProvider dir={directionOf(locale)}>{children}</DirectionProvider>
      </IntlProvider>
    </LocaleContext.Provider>
  );
}
